import React, { useRef, useState } from "react";
import { LayoutDashboard, Video, UserCircle } from "lucide-react";
import { AppHeader } from "@/components/reelflo/AppHeader";
import { NeonLogo } from "@/components/reelflo/NeonLogo";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

// Exactly three destinations — no fourth destination, no help affordance.
const NAV_ITEMS = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "app-nav-dashboard" },
    { key: "videos", label: "Finished Videos", icon: Video, testId: "app-nav-videos" },
    { key: "account", label: "Account", icon: UserCircle, testId: "app-nav-account" },
];

/**
 * Authenticated app shell. Owns the three route destinations, the drawer
 * state, and the single-instance rightSlot. Presentational (no react-router):
 * the parent page supplies navigation handlers and the current `active` key.
 *
 * - lg and above: persistent sidebar (canonical nav testids live there).
 * - below lg: mobile top bar (AppHeader = wordmark + drawer trigger) and a
 *   drawer with the same three destinations (no testids; role + name).
 * - rightSlot renders exactly once: hidden below `sm`, visible at `sm` and
 *   above (BASE behavior), positioned beside the top bar at sm-to-lg and in
 *   the shell utility area at lg+.
 */
export const AppShell = ({ active, onLogoClick, onNavigate, rightSlot, children }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const openerRef = useRef(null);
    const firstDrawerLinkRef = useRef(null);

    const openDrawer = () => {
        openerRef.current = document.activeElement;
        setDrawerOpen(true);
    };

    // Deterministic focus return to the opener whenever the drawer closes.
    // Deferred one tick so the dialog's focus trap releases first.
    const handleDrawerChange = (open) => {
        setDrawerOpen(open);
        if (!open && openerRef.current && typeof openerRef.current.focus === "function") {
            const opener = openerRef.current;
            setTimeout(() => opener.focus(), 0);
        }
    };

    const go = (key) => {
        handleDrawerChange(false);
        onNavigate && onNavigate(key);
    };

    const navButton = ({ key, label, icon: Icon, testId }, { withTestId, large, ref }) => {
        const isActive = active === key;
        return (
            <button
                key={key}
                ref={ref}
                type="button"
                {...(withTestId ? { "data-testid": testId } : {})}
                aria-label={label}
                aria-current={isActive ? "page" : undefined}
                onClick={() => go(key)}
                className={cn(
                    "inline-flex items-center gap-3 rounded-lg font-semibold",
                    large ? "min-h-12 px-4 text-base" : "min-h-11 px-3 text-sm",
                    "transition-colors duration-200",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60",
                    isActive
                        ? "bg-white/10 text-white"
                        : "text-white/60 hover:bg-white/[0.06] hover:text-white",
                )}
            >
                <Icon
                    className={cn(
                        large ? "h-5 w-5" : "h-4 w-4",
                        "flex-none",
                        isActive ? "text-purple-300" : "",
                    )}
                />
                {label}
            </button>
        );
    };

    return (
        <div className="relative z-10 lg:flex">
            {/* Desktop sidebar — wordmark + the three destinations, nothing else. */}
            <aside className="sticky top-0 z-40 hidden h-screen w-60 flex-none border-r border-white/10 bg-surface-panel lg:block">
                <div className="flex h-full flex-col gap-6 px-3 py-5">
                    <NeonLogo
                        data-testid="app-sidebar-logo"
                        onClick={onLogoClick}
                        className="mx-2 self-start text-2xl"
                    />
                    <nav aria-label="Primary" className="flex flex-col gap-1">
                        {NAV_ITEMS.map((item) =>
                            navButton(item, { withTestId: true, large: false, ref: undefined }),
                        )}
                    </nav>
                </div>
            </aside>

            <div className="min-w-0 flex-1">
                {/* Top bar: wordmark + drawer trigger below lg. The single
                    rightSlot element sits beside it (hidden below sm) and, at
                    lg+, becomes the shell utility area. */}
                <header
                    className={cn(
                        "sticky top-0 z-40 border-b border-white/10 bg-black/50 backdrop-blur-md",
                        !rightSlot && "lg:hidden",
                    )}
                >
                    <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-2 px-4 py-2 sm:gap-3 sm:px-6">
                        <div className="lg:hidden">
                            <AppHeader
                                onLogoClick={onLogoClick}
                                onMenuClick={openDrawer}
                            />
                        </div>
                        <div
                            data-testid="app-header-right-slot"
                            className="ml-auto hidden items-center gap-3 sm:flex sm:gap-4"
                        >
                            {rightSlot}
                        </div>
                    </div>
                </header>

                {children}
            </div>

            {/* Mobile drawer — same three destinations, no testids here. */}
            <Drawer open={drawerOpen} onOpenChange={handleDrawerChange} shouldScaleBackground={false}>
                <DrawerContent
                    aria-describedby={undefined}
                    onOpenAutoFocus={(e) => {
                        e.preventDefault();
                        if (firstDrawerLinkRef.current) firstDrawerLinkRef.current.focus();
                    }}
                    className="border-white/10 bg-surface-raised"
                >
                    <DrawerTitle className="px-6 pt-3 text-sm font-semibold uppercase tracking-wide text-white/45">
                        Navigation
                    </DrawerTitle>
                    <nav aria-label="Drawer" className="flex flex-col gap-1 p-4 pb-8">
                        {NAV_ITEMS.map((item, i) =>
                            navButton(item, {
                                withTestId: false,
                                large: true,
                                ref: i === 0 ? firstDrawerLinkRef : undefined,
                            }),
                        )}
                    </nav>
                </DrawerContent>
            </Drawer>
        </div>
    );
};
